// ════════════════════════════════════════════════════════════════════
// Glass-i — Customer Price Lists register (Stage 6PL-1b).
//
// The list of cards. One card opens as its own PAGE
// (/erp/masters/price-lists/:id, PriceListDetailPage) rather than a drawer:
// a card is a workspace — five rate grids, assignments, history — not a
// quick edit, and a route gives it a shareable URL and browser back.
//
// GATING — every WRITE is master-only server-side (decision D7). This page
// hides the write controls from a non-master as a courtesy; the gate that
// matters is `requireMaster` on the route. A sub-user still gets full read
// access, because explaining a quotation they cannot change is exactly their
// job.
// ════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, App, Button, Card, DatePicker, Empty, Form, Input,
  Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography
} from 'antd';
import { PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageHeader from '../../../../components/PageHeader';
import useAuth from '../../../../hooks/useAuth';
import priceListApi from './priceListApi';
import {
  API_DATE, DATE_FMT, STATUS_TAG, toApiDate, isExpired, pager, errMessage, GRID_CSS
} from './priceListShared';

const { Text } = Typography;

// The rate kinds, shortened for the register's coverage column. The card page
// spells them out in full (KINDS); here the count is the fact and the kind is
// only context, so the long form would crowd out the number.
const KIND_SHORT = {
  ITEM: 'items', FABRICATION: 'fab', PROCESS: 'process',
  OTHER_CHARGE: 'charges', MANUAL_CHARGE: 'manual'
};

const dash = <Text type="secondary">—</Text>;

/** `01-04-2026 → open`, or a single dash when the card is always in force. */
function EffectiveWindow({ row }) {
  const from = row.effectiveFrom ? dayjs(row.effectiveFrom, API_DATE).format(DATE_FMT) : null;
  const to = row.effectiveTo ? dayjs(row.effectiveTo, API_DATE).format(DATE_FMT) : null;
  if (!from && !to) {
    return <Tooltip title="No dates set — this card is in force whenever it is active.">{dash}</Tooltip>;
  }
  return (
    <span className="erp-grid__muted" style={{ whiteSpace: 'nowrap' }}>
      {from || 'always'} <span style={{ color: '#CBD5E1' }}>→</span> {to || 'open'}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
// The register
// ════════════════════════════════════════════════════════════════════
export default function PriceListsPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { me } = useAuth();
  const isMaster = me?.isMaster === true;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await priceListApi.list({ status, search: search || undefined, limit: 200 });
      setRows((res && res.rows) || []);
    } catch (e) {
      message.error(errMessage(e, 'Could not load price lists.'));
    } finally { setLoading(false); }
  }, [status, search, message]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const v = await createForm.validateFields();
      setCreating(true);
      const made = await priceListApi.create({
        listCode: v.listCode,
        listName: v.listName,
        description: v.description || null,
        effectiveFrom: toApiDate(v.effectiveFrom),
        effectiveTo: toApiDate(v.effectiveTo),
        cloneFromId: v.cloneFromId || null
      });
      setCreateOpen(false);
      createForm.resetFields();
      message.success('Created as a draft — it prices nothing until you activate it.');
      navigate(`/erp/masters/price-lists/${made.id}`);
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(errMessage(e, 'Could not create the price list.'));
    } finally { setCreating(false); }
  };

  const archive = async (id) => {
    try {
      await priceListApi.archive(id);
      message.success('Archived. Documents already saved keep their rates.');
      load();
    } catch (e) { message.error(errMessage(e, 'Could not archive.')); }
  };

  // EVERY ROW IS ONE LINE. That is the whole of "compact" for a register — a
  // second line in any cell sets the height of every row in the table, and two
  // stacked lines cost more than all the padding in the grid put together.
  // Detail that does not earn a column (the description, the per-kind split)
  // moves into a tooltip on the cell it belongs to.
  const columns = useMemo(() => [
    {
      title: 'Code',
      dataIndex: 'listCode',
      width: 140,
      render: (v) => <Text strong style={{ fontSize: 13 }}>{v}</Text>
    },
    {
      // The description rides on the SAME line as the name rather than under
      // it. The flex column has ~500px of slack at desktop width, so this costs
      // nothing and fills it with the one thing an operator actually wants when
      // choosing between two cards — what the agreement was. The cell ellipsises
      // as one string, so a long description can never add a second line.
      title: 'Name',
      dataIndex: 'listName',
      ellipsis: true,
      render: (v, r) => {
        const cell = (
          <>
            {v}
            {r.description ? <span className="erp-grid__muted">{'  ·  '}{r.description}</span> : null}
          </>
        );
        return r.description
          ? <Tooltip title={r.description} placement="topLeft">{cell}</Tooltip>
          : cell;
      }
    },
    {
      // The count is the scannable fact; WHICH kinds it covers is a follow-up
      // question, so it lives in the tooltip rather than in 190px of clipped
      // text that could not finish a sentence anyway.
      title: 'Rates',
      dataIndex: 'rateCount',
      width: 84,
      align: 'right',
      render: (v, r) => {
        const n = v || 0;
        if (!n) {
          return (
            <Tooltip title="No rates yet — an empty card cannot be activated.">
              <span className="erp-grid__muted">none</span>
            </Tooltip>
          );
        }
        const split = Object.entries(r.kindSplit || {})
          .map(([k, c]) => `${c} ${KIND_SHORT[k] || k.toLowerCase()}`)
          .join(' · ');
        return (
          <Tooltip title={split || null}>
            <span style={{ fontWeight: 600 }}>{n}</span>
          </Tooltip>
        );
      }
    },
    {
      // "On" read as a cryptic abbreviation in the header band; this column
      // answers "how many customers is this card pricing for?".
      title: 'Assigned',
      dataIndex: 'assignmentCount',
      width: 92,
      align: 'right',
      render: (v) => (v
        ? <Tooltip title={`${v} customer/group assignment${v === 1 ? '' : 's'}`}><span>{v}</span></Tooltip>
        : dash)
    },
    {
      title: 'Effective',
      key: 'window',
      width: 196,
      render: (_v, r) => <EffectiveWindow row={r} />
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 132,
      render: (v, r) => (
        // Space, not bare siblings: without it the two tags render as one word
        // ("ActiveExpired"), which is how it read before this was caught.
        <Space size={4} wrap={false}>
          <Tooltip title={STATUS_TAG[v]?.hint}>
            <Tag color={STATUS_TAG[v]?.color} style={{ marginInlineEnd: 0 }}>{STATUS_TAG[v]?.text}</Tag>
          </Tooltip>
          {v === 'ACTIVE' && isExpired(r) && (
            <Tooltip title="Its end date has passed, so it no longer prices anything.">
              <Tag color="red" style={{ marginInlineEnd: 0 }}>Expired</Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: '',
      key: 'actions',
      width: isMaster ? 72 : 40,
      align: 'right',
      fixed: 'right',
      // The row itself opens the card, so these must not also fire it.
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_v, r) => (
        <Space size={0}>
          <Tooltip title={isMaster ? 'Open' : 'View'}>
            <Button type="text" size="small" icon={<EditOutlined />}
              onClick={() => navigate(`/erp/masters/price-lists/${r.id}`)} />
          </Tooltip>
          {isMaster && r.status !== 'ARCHIVED' && (
            <Popconfirm
              title="Archive this price list?"
              description="It stops pricing new documents. Everything already saved keeps its rates."
              onConfirm={() => archive(r.id)}
            >
              <Tooltip title="Archive"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ], [isMaster]);

  return (
    <>
      <PageHeader
        title="Price Lists"
        subtitle="Per-customer rates for glass, fabrication, processes and charges. A list holds only what differs from the master — everything else follows your standard price."
        actions={isMaster ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New price list
          </Button>
        ) : null}
      />

      {/* One card, not two. The filters belong to the table they filter, and a
          second card around two controls was pure chrome between the heading
          and the data. */}
      <Card size="small" className="pl-grid">
        <style>{GRID_CSS}</style>

        <div className="pl-toolbar">
          <Input.Search
            allowClear
            size="small"
            placeholder="Search code or name…"
            style={{ width: 260 }}
            onSearch={setSearch}
          />
          <Select
            value={status}
            size="small"
            style={{ width: 150 }}
            onChange={setStatus}
            options={[
              { value: 'ALL', label: 'All statuses' },
              { value: 'DRAFT', label: 'Draft' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'ARCHIVED', label: 'Archived' }
            ]}
          />
        </div>

        <Table
          rowKey="id"
          size="small"
          className="erp-grid erp-grid--compact"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={pager(25)}
          scroll={{ x: 880 }}
          rowClassName="pl-row"
          onRow={(r) => ({ onClick: () => navigate(`/erp/masters/price-lists/${r.id}`) })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={(
                  <span>
                    No price lists yet — every customer is quoted at your master rates.
                    <br />
                    <Text type="secondary">Create one when a group of customers has negotiated something different.</Text>
                  </span>
                )}
              />
            )
          }}
        />
      </Card>

      <Modal
        open={createOpen}
        title="New price list"
        okText="Create as draft"
        confirmLoading={creating}
        onOk={create}
        onCancel={() => setCreateOpen(false)}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="A new list is always a draft."
          description="It prices nothing until you activate it, so a half-entered card can never reach a quotation."
        />
        <Form form={createForm} layout="vertical">
          <Form.Item
            label="List code"
            name="listCode"
            rules={[
              { required: true, message: 'A code is required.' },
              { pattern: /^[A-Za-z0-9][A-Za-z0-9_-]*$/, message: 'Letters, digits, hyphen and underscore only.' }
            ]}
            extra="Short and stable — it cannot be changed once the list is live."
          >
            <Input placeholder="DEALER-2026" />
          </Form.Item>
          <Form.Item label="List name" name="listName" rules={[{ required: true, message: 'A name is required.' }]}>
            <Input placeholder="Dealer network" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="The agreement, the meeting, the approval reference." />
          </Form.Item>
          <Space size="large">
            <Form.Item label="Effective from" name="effectiveFrom"><DatePicker format={DATE_FMT} /></Form.Item>
            <Form.Item label="Effective to" name="effectiveTo"><DatePicker format={DATE_FMT} placeholder="open-ended" /></Form.Item>
          </Space>
          <Form.Item
            label="Start from"
            name="cloneFromId"
            extra="Copies the rate rows only. Assignments are never copied, so a copy can never start pricing someone by accident."
          >
            <Select
              allowClear
              placeholder="Empty list"
              options={rows
                .filter((r) => r.status !== 'ARCHIVED')
                .map((r) => ({ value: r.id, label: `${r.listCode} — ${r.rateCount || 0} rate(s)` }))}
              suffixIcon={<CopyOutlined />}
            />
          </Form.Item>
        </Form>
      </Modal>

    </>
  );
}
